/**
 * The shortcut model both hosts run on (web ticket 10, one-window ticket 01,
 * FR-52/FR-64, stories 36–38, 43).
 *
 * This was the browser's answer to a separate desktop model, which ADR-0018
 * replaced. The two differed in four ways: how they register, what is
 * configurable, which keys, and which commands. Three of those stopped being
 * true the moment the desktop's keys became in-window shortcuts — nothing
 * registers with the operating system, both hosts are rebindable, and both want
 * bare keys in a focused surface that contains a text box. What is left is
 * *which commands*, and that is one column wide.
 *
 * So there is one table with a {@link ShortcutHost} column, and one set of
 * matchers over it. Two rules survive from the web's model and now cover both
 * hosts:
 *
 * - **The keys are unmodified, and only safe because of {@link typingInto}.**
 *   The `Ctrl+Alt` prefix the desktop's global hotkeys carried existed to stay
 *   out of every other application's way. Nothing is claimed system-wide now, so
 *   it buys nothing and costs something — `Ctrl+Alt` is AltGr on a Windows
 *   keyboard — and a bare key is what the prompter wants anyway.
 * - **Nothing can fail to register, and something worse can happen instead.**
 *   The failure is a key the *host window* also acts on, which opens a browser
 *   feature or a menu over the top of a take. That is what the reserved tables
 *   exist for, and it is checked before shipping rather than reported afterwards.
 *
 * **Every function that reads the table takes a host's rows as its first
 * parameter, and none of them has a default.** That is not decoration.
 * `settings.shortcuts` is a single blob shared by both hosts, so a desktop-only
 * `markGood` chord must not be able to collide with a web binding, and a
 * defaulted parameter is exactly how that bug gets in.
 *
 * Everything here is a pure function over plain data, so a page can match a
 * keypress with it and a controller can validate a rebinding with it, and
 * neither needs a DOM to be tested. It lives in the core because the settings
 * blob these are stored in, and the rules for reading it back, are the core's.
 */

/** Which surface a row belongs to. */
export type ShortcutHost = "desktop" | "web";

/**
 * The commands a shortcut can be bound to, across both hosts.
 *
 * Four of them describe a window that sits over other applications — the two
 * opacity steps — or write a note about footage the desktop is recording to
 * disk, and only the desktop has either. The `hosts` column on each row is what
 * says so; this union is the whole set.
 */
export type ShortcutCommand =
  | "record"
  | "pauseResume"
  | "stop"
  | "nudgeBack"
  | "nudgeForward"
  | "restart"
  | "resync"
  | "cycleMode"
  | "fontUp"
  | "fontDown"
  | "opacityUp"
  | "opacityDown"
  | "refresh"
  | "markGood"
  | "markBad";

/**
 * A chord, written down.
 *
 * The key is a `KeyboardEvent.code` — the *physical* key — and not a
 * `KeyboardEvent.key`. That is the one decision in this module that is not
 * obvious and it is load-bearing: `key` is the character the layout produced,
 * so with a modifier held or on any layout that is not US it can be a dead key,
 * an accented letter, or an empty string. The physical key is the one the user
 * pressed and the one printed on the keycap they will look for.
 *
 * Modifiers come first, always in the order below, so that one chord has exactly
 * one spelling and two of them can be compared as strings.
 */
export type Chord = string;

/** The modifier tokens, in the order a chord writes them. */
const MODIFIERS = ["Control", "Alt", "Shift", "Meta"] as const;

/** One command's identity: what to call it, the key it is on out of the box, and
 * which hosts have any use for it. */
export interface ShortcutSpec {
  command: ShortcutCommand;
  /** The name shown on the shortcuts screen. */
  label: string;
  defaultChord: Chord;
  /** The hosts this row exists on. Never empty — a command no host can run is a
   * row nobody can bind, and a test holds the table to it. */
  hosts: readonly ShortcutHost[];
}

const BOTH_HOSTS = ["desktop", "web"] as const satisfies readonly ShortcutHost[];
const DESKTOP_ONLY = ["desktop"] as const satisfies readonly ShortcutHost[];

/**
 * Every command and its default key, in the order the shortcuts screen lists
 * them — transport, then position, then the display, then the take markers.
 *
 * The two that end a take or re-fetch the script mid-read are deliberately not
 * next to the ones you reach for constantly: `S` is across the keyboard from the
 * arrows, and refresh is on `F2`, which nothing is pressed by accident. The
 * markers are last because they are the only two commands that do nothing to the
 * prompter or the transport — they write a note about the footage and leave the
 * take exactly as it was, and `G` and `B` are the words rather than a mnemonic
 * anyone has to be taught.
 *
 * **The web's rows are in the same order they were before the desktop joined the
 * table**, because that order is what its settings screen renders and what its
 * browser tests sweep.
 */
export const ALL_SHORTCUTS: readonly ShortcutSpec[] = [
  { command: "record", label: "Start recording", defaultChord: "KeyR", hosts: BOTH_HOSTS },
  { command: "pauseResume", label: "Pause / resume", defaultChord: "Space", hosts: BOTH_HOSTS },
  { command: "stop", label: "Stop recording", defaultChord: "KeyS", hosts: BOTH_HOSTS },
  {
    command: "nudgeBack",
    label: "Nudge back one line",
    defaultChord: "ArrowUp",
    hosts: BOTH_HOSTS,
  },
  {
    command: "nudgeForward",
    label: "Nudge forward one line",
    defaultChord: "ArrowDown",
    hosts: BOTH_HOSTS,
  },
  // `Home` because that is what `Home` means everywhere else, and because it is
  // nowhere near the keys pressed mid-take: this is a between-takes control.
  {
    command: "restart",
    label: "Back to the top of the script",
    defaultChord: "Home",
    hosts: BOTH_HOSTS,
  },
  {
    command: "resync",
    label: "Re-sync to nearest match",
    defaultChord: "Enter",
    hosts: BOTH_HOSTS,
  },
  { command: "cycleMode", label: "Cycle scroll mode", defaultChord: "KeyM", hosts: BOTH_HOSTS },
  { command: "fontUp", label: "Font size up", defaultChord: "Equal", hosts: BOTH_HOSTS },
  { command: "fontDown", label: "Font size down", defaultChord: "Minus", hosts: BOTH_HOSTS },
  // The window's own opacity, which a tab has nothing to do with: a browser tab
  // is not a window over anything.
  {
    command: "opacityUp",
    label: "Opacity up",
    defaultChord: "BracketRight",
    hosts: DESKTOP_ONLY,
  },
  {
    command: "opacityDown",
    label: "Opacity down",
    defaultChord: "BracketLeft",
    hosts: DESKTOP_ONLY,
  },
  { command: "refresh", label: "Refresh script", defaultChord: "F2", hosts: BOTH_HOSTS },
  {
    command: "markGood",
    label: "Mark this moment good",
    defaultChord: "KeyG",
    hosts: DESKTOP_ONLY,
  },
  {
    command: "markBad",
    label: "Mark this moment bad",
    defaultChord: "KeyB",
    hosts: DESKTOP_ONLY,
  },
] as const;

const BY_HOST: Record<ShortcutHost, readonly ShortcutSpec[]> = {
  desktop: ALL_SHORTCUTS.filter((spec) => spec.hosts.includes("desktop")),
  web: ALL_SHORTCUTS.filter((spec) => spec.hosts.includes("web")),
};

/**
 * One host's rows, in table order.
 *
 * Computed once rather than filtered per call: this is read on every keypress by
 * {@link commandFor}, and the identity being stable is what lets a caller hold on
 * to it.
 */
export function shortcutsFor(host: ShortcutHost): readonly ShortcutSpec[] {
  return BY_HOST[host];
}

/**
 * The web's rows, under the name they had before the desktop joined the table.
 *
 * Kept for the web's browser tests, which sweep this list and are required to pass
 * unedited. Nothing in either host's screens reads it — they go through
 * {@link shortcutsFor}, which is what a new caller should use.
 */
export const SHORTCUTS: readonly ShortcutSpec[] = BY_HOST.web;

const COMMAND_SET = new Set<string>(ALL_SHORTCUTS.map((spec) => spec.command));

export function isShortcutCommand(value: unknown): value is ShortcutCommand {
  return typeof value === "string" && COMMAND_SET.has(value);
}

/** A command's display name, for the shortcuts screen. */
export function shortcutLabel(command: ShortcutCommand): string {
  return ALL_SHORTCUTS.find((spec) => spec.command === command)?.label ?? command;
}

/**
 * Which key each command is on.
 *
 * Total over the commands of the host it was built from, which is the thing that
 * matters and the thing the type cannot say: a set of web bindings has no
 * `markGood` in it at all. Every function below takes that host's rows, so a
 * lookup only ever happens against a command the bindings actually carry.
 */
export type ShortcutBindings = Record<ShortcutCommand, Chord>;

/** What a host with no stored settings starts from, and what reset restores
 * (FR-64). */
export function defaultBindings(
  specs: readonly ShortcutSpec[],
): ShortcutBindings {
  return Object.fromEntries(
    specs.map((spec) => [spec.command, spec.defaultChord]),
  ) as ShortcutBindings;
}

/** The web's built-ins. See {@link SHORTCUTS}. */
export const DEFAULT_SHORTCUT_BINDINGS: ShortcutBindings = defaultBindings(
  BY_HOST.web,
);

/** What the shortcuts screen is a view onto: which key every command is on. */
export interface ShortcutState {
  bindings: ShortcutBindings;
}

export const DEFAULT_SHORTCUT_STATE: ShortcutState = {
  bindings: DEFAULT_SHORTCUT_BINDINGS,
};

/**
 * A key the host window has its own use for, and what that use is.
 *
 * Knowable in advance and worse than a binding that simply fails: a reserved key
 * does not do nothing, it opens a browser feature or a menu over the top of
 * whatever was being filmed. So it is a table, checked against the defaults by a
 * test and against a rebinding by {@link reservedReason}.
 *
 * `what` is a whole sentence and says which host is doing it, because the two
 * hosts are not the same thing doing the same thing: a tab is inside a browser
 * with its own accelerators, and an Electron window is inside Windows.
 *
 * The distinction the sentences draw is whether the key reaches the page at all.
 * One that never does gives a shortcut that silently does nothing *and* a window
 * you did not want; the rest reach the page and would have to be swallowed from
 * under the host, which is not a thing to do to somebody's Ctrl+S by default.
 *
 * Neither list is exhaustive — extensions, platform conventions and locale
 * layouts add to both — which is why they decide only what cuekards will *not*
 * offer, and never what is safe.
 */
export interface ReservedKey {
  chord: Chord;
  /** A whole sentence: what happens if this key is bound, in words a user can
   * act on. */
  what: string;
}

/**
 * Chromium's own accelerators, on Windows and Linux, which is what the spec
 * supports.
 *
 * **Two limits worth knowing before trusting it.** It is written from documented
 * behaviour rather than measured, because a synthetic keypress always reaches
 * the page and so cannot tell a free key from one the browser would have eaten;
 * only a person pressing keys in a real window can settle that. And the chords
 * here are physical keys while the browser's own accelerators follow the
 * *layout*, so on a keyboard that is not US the two do not line up exactly —
 * which is a reason to keep the defaults clear of the browser's space
 * altogether rather than to trust the boundary.
 */
export const BROWSER_RESERVED: readonly ReservedKey[] = [
  // Never reaches the page.
  {
    chord: "Control+KeyT",
    what: "the browser opens a new tab with this and never passes it to the page",
  },
  {
    chord: "Control+KeyN",
    what: "the browser opens a new window with this and never passes it to the page",
  },
  {
    chord: "Control+KeyW",
    what: "the browser closes the tab with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+KeyT",
    what: "the browser reopens the last closed tab with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+KeyN",
    what: "the browser opens an incognito window with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+KeyW",
    what: "the browser closes the window with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+KeyQ",
    what: "the browser quits with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+KeyI",
    what: "the browser opens the developer tools with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+KeyJ",
    what: "the browser opens the developer console with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+Delete",
    what: "the browser opens clear browsing data with this and never passes it to the page",
  },
  {
    chord: "Control+Tab",
    what: "the browser switches to the next tab with this and never passes it to the page",
  },
  {
    chord: "Control+Shift+Tab",
    what: "the browser switches to the previous tab with this and never passes it to the page",
  },
  {
    chord: "Alt+F4",
    what: "Windows closes the window with this and never passes it to the page",
  },
  {
    chord: "F11",
    what: "the browser leaves full screen with this and never passes it to the page",
  },
  {
    chord: "F12",
    what: "the browser opens the developer tools with this and never passes it to the page",
  },
  {
    chord: "Escape",
    what: "the browser leaves full screen with this and never passes it to the page",
  },
  // Reaches the page, but the browser acts too unless the page swallows it.
  { chord: "Control+KeyR", what: "the browser reloads the page with this" },
  { chord: "Control+KeyS", what: "the browser saves the page with this" },
  { chord: "Control+KeyP", what: "the browser opens the print dialog with this" },
  { chord: "Control+KeyF", what: "the browser opens the find bar with this" },
  { chord: "Control+KeyG", what: "the browser finds the next match with this" },
  { chord: "Control+KeyD", what: "the browser bookmarks the page with this" },
  { chord: "Control+KeyO", what: "the browser opens a file with this" },
  { chord: "Control+KeyU", what: "the browser shows the page source with this" },
  { chord: "Control+KeyH", what: "the browser opens the history with this" },
  { chord: "Control+KeyJ", what: "the browser opens the downloads with this" },
  { chord: "Control+KeyL", what: "the browser jumps to the address bar with this" },
  { chord: "Control+Equal", what: "the browser zooms the page in with this" },
  { chord: "Control+Minus", what: "the browser zooms the page out with this" },
  { chord: "Control+Digit0", what: "the browser resets the page zoom with this" },
  { chord: "Alt+ArrowLeft", what: "the browser goes back with this" },
  { chord: "Alt+ArrowRight", what: "the browser goes forward with this" },
  { chord: "Alt+Home", what: "the browser goes to the home page with this" },
  { chord: "Alt+KeyD", what: "the browser jumps to the address bar with this" },
  { chord: "Alt+KeyE", what: "the browser opens its menu with this" },
  { chord: "Alt+KeyF", what: "the browser opens its menu with this" },
  { chord: "F1", what: "the browser opens its help with this" },
  { chord: "F3", what: "the browser opens the find bar with this" },
  { chord: "F5", what: "the browser reloads the page with this" },
  {
    chord: "F6",
    what: "the browser moves focus to its own controls with this",
  },
  { chord: "F7", what: "the browser turns caret browsing on with this" },
  { chord: "F10", what: "the browser opens its menu with this" },
] as const;

/**
 * What an ordinary Windows application window will not give up.
 *
 * **Short on purpose.** Most of {@link BROWSER_RESERVED} is Chromium's
 * *browser-UI* accelerators, and an Electron window has no tab bar, address bar,
 * bookmarks or find bar — the entries about them are simply false here. The rest
 * of Chromium's list came from the default application menu, and ticket 06
 * removes that menu outright, which is what makes this table the truth rather
 * than a guess.
 *
 * What is left is the window manager's, and no application gets to have it.
 */
export const DESKTOP_RESERVED: readonly ReservedKey[] = [
  {
    chord: "Alt+F4",
    what: "Windows closes the window with this and never passes it to the app",
  },
  {
    chord: "Alt+Space",
    what: "Windows opens the window menu with this and never passes it to the app",
  },
  {
    chord: "F10",
    what: "Windows gives this to the window's menu bar",
  },
  {
    // Never arrives as a chord — `chordFromEvent` reads a bare `AltLeft` as a
    // modifier going down and returns null — so this row can only ever be read.
    // It is here because the table is meant to be the truth about the keys a
    // window cannot have, and a reader checking whether Alt is safe deserves an
    // answer.
    chord: "Alt",
    what: "Windows gives a bare Alt to the window's menu bar",
  },
] as const;

/** One host's reserved keys. */
export function reservedFor(host: ShortcutHost): readonly ReservedKey[] {
  return host === "desktop" ? DESKTOP_RESERVED : BROWSER_RESERVED;
}

const RESERVED: Record<ShortcutHost, Map<Chord, ReservedKey>> = {
  desktop: new Map(DESKTOP_RESERVED.map((reserved) => [reserved.chord, reserved])),
  web: new Map(BROWSER_RESERVED.map((reserved) => [reserved.chord, reserved])),
};

/**
 * Why a chord cannot be used on this host, or null when it is free.
 *
 * A sentence rather than a flag, because "you cannot have that key" is not
 * something anybody can act on and "Chrome reloads the page" is.
 */
export function reservedReason(host: ShortcutHost, chord: Chord): string | null {
  return RESERVED[host].get(chord)?.what ?? null;
}

/** The five fields of a `KeyboardEvent` a chord is read out of. Structural, so
 * the matcher is testable without a DOM and usable from a real event. */
export interface KeyEventLike {
  code: string;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
}

/** The physical keys that are only ever modifiers. Held on the way to a chord,
 * each of these fires a keydown of its own, and none of them is a binding
 * anybody meant to press. */
const MODIFIER_CODES = new Set([
  "ControlLeft",
  "ControlRight",
  "AltLeft",
  "AltRight",
  "ShiftLeft",
  "ShiftRight",
  "MetaLeft",
  "MetaRight",
  "CapsLock",
]);

/** The chord a keypress is, or null when it is only a modifier going down. */
export function chordFromEvent(event: KeyEventLike): Chord | null {
  if (event.code === "" || MODIFIER_CODES.has(event.code)) return null;
  const held: string[] = [];
  if (event.ctrlKey) held.push("Control");
  if (event.altKey) held.push("Alt");
  if (event.shiftKey) held.push("Shift");
  if (event.metaKey) held.push("Meta");
  return [...held, event.code].join("+");
}

/** How each modifier is labelled on a Windows keyboard. */
const MODIFIER_DISPLAY: Record<(typeof MODIFIERS)[number], string> = {
  Control: "Ctrl",
  Alt: "Alt",
  Shift: "Shift",
  Meta: "Win",
};

/** Codes whose keycap says something other than the code does. Anything not
 * here is shown as it is stored, which is right for `F2` and harmless for a key
 * nobody has bound. */
const KEY_DISPLAY: Record<string, string> = {
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Equal: "=",
  Minus: "-",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Backquote: "`",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Escape: "Esc",
};

/** One key token as a keyboard is labelled: `KeyM` is `M`, `Digit1` is `1`. */
function displayKey(code: string): string {
  if (code.startsWith("Key") && code.length === 4) return code.slice(3);
  if (code.startsWith("Digit") && code.length === 6) return code.slice(5);
  return KEY_DISPLAY[code] ?? code;
}

/** A chord written the way the keys are labelled, for display only —
 * `Control+Shift+KeyM` reads as `Ctrl + Shift + M`. */
export function formatChord(chord: Chord): string {
  const tokens = chord.split("+");
  const key = tokens.pop() ?? "";
  return [
    ...tokens.map(
      (token) => MODIFIER_DISPLAY[token as (typeof MODIFIERS)[number]] ?? token,
    ),
    displayKey(key),
  ].join(" + ");
}

/**
 * The command a keypress runs on this host, or null when nothing is bound to it.
 *
 * Exact on the modifiers, not merely on the key: `Ctrl+M` is the browser's or
 * the machine's business, and a matcher that fired `cycleMode` on it would take
 * a key it was never given.
 */
export function commandFor(
  specs: readonly ShortcutSpec[],
  bindings: ShortcutBindings,
  event: KeyEventLike,
): ShortcutCommand | null {
  const chord = chordFromEvent(event);
  if (chord === null) return null;
  for (const spec of specs) {
    if (bindings[spec.command] === chord) return spec.command;
  }
  return null;
}

/** Where an event landed, reduced to what {@link typingInto} reads. */
export interface EventTargetLike {
  tagName?: string;
  isContentEditable?: boolean;
}

/**
 * Whether a keypress belongs to whatever the user is typing into rather than to
 * the prompter.
 *
 * The rule the unmodified defaults depend on. Without it, pasting a script into
 * the box would record a take and cycle the scroll mode on the way past — which
 * is not a subtle bug but it is an invisible one until somebody types an `s`.
 */
export function typingInto(target: EventTargetLike | null): boolean {
  if (target === null) return false;
  if (target.isContentEditable === true) return true;
  const tag = target.tagName ?? "";
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * The command a chord is already bound to on this host, other than `command`
 * itself, or null when it is free.
 *
 * Only this host's rows, which is the point of the parameter: the desktop's
 * `markGood` is on `G`, and a web user putting `record` there is not in conflict
 * with anything their tab can do.
 */
export function bindingConflict(
  specs: readonly ShortcutSpec[],
  bindings: ShortcutBindings,
  command: ShortcutCommand,
  chord: Chord,
): ShortcutCommand | null {
  for (const spec of specs) {
    if (spec.command === command) continue;
    if (bindings[spec.command] === chord) return spec.command;
  }
  return null;
}

/** What is written down: only this host's bindings that differ from the
 * built-ins.
 *
 * The built-ins are not copied into anybody's settings file, so changing them
 * later reaches the people who never touched their shortcuts instead of being
 * shadowed by a stale copy. */
export function storedBindings(
  specs: readonly ShortcutSpec[],
  bindings: ShortcutBindings,
): Record<string, Chord> {
  const stored: Record<string, Chord> = {};
  for (const spec of specs) {
    if (bindings[spec.command] !== spec.defaultChord) {
      stored[spec.command] = bindings[spec.command];
    }
  }
  return stored;
}

/**
 * Read whatever was stored into a complete set of this host's bindings.
 *
 * Total by construction and biased towards the built-ins in every doubtful case:
 * a blob that is corrupt, truncated or hand-edited into nonsense leaves every
 * shortcut working, which is the failure that costs the user nothing. The other
 * way round, a bad file would quietly take the record key away.
 *
 * Three rules beyond "is it a string", and all of them are about a blob that was
 * valid when it was written:
 *
 * - **Another host's command is not this host's business.** The blob is shared,
 *   so a desktop `markGood` override is in the file a browser reads. It is
 *   skipped rather than resolved, which is what keeps it from colliding with a
 *   web binding that has every right to that key.
 * - **A stored chord is re-checked against the host's own keys.** Browsers move,
 *   and a key that was free when it was bound can stop being free. The
 *   alternative is the user finding out mid-take.
 * - **A collision drops the *override*, not the built-in.** Two commands on one
 *   key is the one state that cannot be allowed out of here, because the matcher
 *   would run whichever it reached first.
 */
export function resolveBindings(
  specs: readonly ShortcutSpec[],
  reserved: readonly ReservedKey[],
  saved: unknown,
): ShortcutBindings {
  const mine = new Set<string>(specs.map((spec) => spec.command));
  const refused = new Set<Chord>(reserved.map((key) => key.chord));
  const overrides = new Map<ShortcutCommand, Chord>();
  if (typeof saved === "object" && saved !== null && !Array.isArray(saved)) {
    for (const [command, chord] of Object.entries(saved as Record<string, unknown>)) {
      if (!isShortcutCommand(command) || !mine.has(command)) continue;
      if (typeof chord !== "string" || chord === "") continue;
      if (refused.has(chord)) continue;
      overrides.set(command, chord);
    }
  }

  // Defaults win, so an override that collides with one is the thing that goes.
  // Dropping one reinstates its own default, which can collide with a *later*
  // override — hence the loop, which shortens by one override each time round.
  for (;;) {
    const used = new Map<Chord, ShortcutCommand>();
    for (const spec of specs) {
      if (!overrides.has(spec.command)) used.set(spec.defaultChord, spec.command);
    }
    let dropped = false;
    for (const spec of specs) {
      const chord = overrides.get(spec.command);
      if (chord === undefined) continue;
      if (used.has(chord)) {
        overrides.delete(spec.command);
        dropped = true;
        break;
      }
      used.set(chord, spec.command);
    }
    if (!dropped) break;
  }

  return Object.fromEntries(
    specs.map((spec) => [
      spec.command,
      overrides.get(spec.command) ?? spec.defaultChord,
    ]),
  ) as ShortcutBindings;
}
